import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    CheckBox,
    DataTable,
    FormField,
    Layer,
    Text,
    TextInput,
} from "grommet";
import { Add } from "grommet-icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context";
import { createFunder, getFunders } from "../../api/funderAllocation";
import { Panel } from "./Panel";
import { ctaButtonProps } from "./ctaButton";

const FunderListSection = () => {
    const { tokens } = useAuth();
    const navigate = useNavigate();

    const [funders, setFunders] = useState([]);
    const [showDeleted, setShowDeleted] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [showCreate, setShowCreate] = useState(false);
    const [createValue, setCreateValue] = useState({ name: "", ref: "" });
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        if (!tokens?.idToken) return;
        setLoading(true);
        setError(null);
        try {
            const data = await getFunders(tokens.idToken, { showDeleted });
            setFunders(data || []);
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to load funders");
        } finally {
            setLoading(false);
        }
    }, [tokens, showDeleted]);

    useEffect(() => {
        load();
    }, [load]);

    const handleCreate = async () => {
        if (!createValue.name) return;
        setSaving(true);
        setError(null);
        try {
            await createFunder(tokens.idToken, {
                name: createValue.name,
                ref: createValue.ref || null,
            });
            setShowCreate(false);
            setCreateValue({ name: "", ref: "" });
            load();
        } catch (e) {
            setError(e?.response?.data?.detail || "Failed to create funder");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box gap="medium">
            <Box direction="row" justify="between" align="center">
                <CheckBox
                    checked={showDeleted}
                    label="Show deleted"
                    onChange={(e) => setShowDeleted(e.target.checked)}
                />
                <Button
                    icon={<Add />}
                    label="New funder"
                    onClick={() => setShowCreate(true)}
                    {...ctaButtonProps}
                />
            </Box>
            {error && <Text color="status-critical">{error}</Text>}
            <Panel>
                {loading ? (
                    <Text>Loading…</Text>
                ) : funders.length === 0 ? (
                    <Text color="dark-5">No funders found</Text>
                ) : (
                    <DataTable
                        columns={[
                            { property: "name", header: "Name" },
                            {
                                property: "ref",
                                header: "Reference",
                                render: (d) => d.ref || "--",
                            },
                            {
                                property: "isActive",
                                header: "Status",
                                render: (d) =>
                                    d.isActive ? "Active" : "Deleted",
                            },
                        ]}
                        data={funders}
                        onClickRow={({ datum }) =>
                            navigate(`/funder-allocation/funders/${datum.id}`)
                        }
                        sortable
                    />
                )}
            </Panel>

            {showCreate && (
                <Layer
                    onEsc={() => setShowCreate(false)}
                    onClickOutside={() => setShowCreate(false)}
                >
                    <Box pad="medium" gap="small" width="medium">
                        <Text weight="bold">New funder</Text>
                        <FormField label="Name" required>
                            <TextInput
                                value={createValue.name}
                                onChange={(e) =>
                                    setCreateValue((v) => ({
                                        ...v,
                                        name: e.target.value,
                                    }))
                                }
                            />
                        </FormField>
                        <FormField label="Reference (optional)">
                            <TextInput
                                value={createValue.ref}
                                onChange={(e) =>
                                    setCreateValue((v) => ({
                                        ...v,
                                        ref: e.target.value,
                                    }))
                                }
                            />
                        </FormField>
                        <Box
                            direction="row"
                            gap="small"
                            justify="end"
                            margin={{ top: "small" }}
                        >
                            <Button
                                label="Cancel"
                                onClick={() => setShowCreate(false)}
                            />
                            <Button
                                label="Create"
                                {...ctaButtonProps}
                                disabled={!createValue.name || saving}
                                onClick={handleCreate}
                            />
                        </Box>
                    </Box>
                </Layer>
            )}
        </Box>
    );
};

export { FunderListSection };
